import ChordsMenu from '@/components/common/menus/ChordsMenu.vue';
import ChordDiagramsMenu from '@/components/common/menus/ChordDiagramsMenu.vue';
import ScrollControl from '@/components/ScrollControl.vue';
import ColumnsMenu from '@/components/common/menus/ColumnsMenu.vue';
import DownloadMenu from '@/components/common/menus/DownloadMenu.vue';
import DuplicateMenu from '@/components/common/menus/DuplicateMenu.vue';
import EditSongMenu from '@/components/common/menus/EditSongMenu.vue';
import MediaMenu from '@/components/common/menus/MediaMenu.vue';
import PresentationMenu from '@/components/common/menus/PresentationMenu.vue';
import NotesMenu from '@/components/common/menus/NotesMenu.vue';
import StudioMenu from '@/components/common/menus/StudioMenu.vue';
import ZoomInOutMenu from '@/components/common/menus/ZoomInOutMenu.vue';
import DropdownTranspose from '@/components/DropDownTranspose.vue';
import AddNotes from '@/components/common/menus/AddNotes.vue';
import DrawingMenu from '@/components/common/menus/DrawingMenu.vue';
import Transpose from '@/components/TriadTranspose.vue';
import DropDownTranspose from '@/components/DropDownTranspose.vue';
import ScrollMenu from '@/components/common/menus/ScrollMenu.vue';
import ShareMenu from '@/components/common/menus/ShareMenu.vue';
import StageSyncMenu from '@/components/common/menus/StageSyncMenu.vue';
import SendPlaylistToTeamMenu from '@/components/common/menus/SendPlaylistToTeamMenu.vue';
import SongMapMenu from '@/components/common/menus/SongMapMenu.vue';
import TunerMenu from '@/components/common/menus/TunerMenu.vue';
import MetronomeMenu from '@/components/common/menus/MetronomeMenu.vue';
import MetronomeIcon from '@/components/icons/MetronomeIcon.vue';
import TuningForkIcon from '@/components/icons/TuningForkIcon.vue';
import { EyeIcon, EyeSlashIcon, DocumentTextIcon, DocumentDuplicateIcon,
    PlayCircleIcon, ViewColumnsIcon, ArrowsPointingOutIcon,
    ArrowDownTrayIcon, ArrowsPointingInIcon, ArrowsUpDownIcon, ArrowDownOnSquareIcon,
    MagnifyingGlassPlusIcon, PresentationChartBarIcon, PaintBrushIcon,
    DocumentPlusIcon, SpeakerWaveIcon, HandRaisedIcon, ShareIcon, PencilIcon,
    MapIcon, CloudIcon, TableCellsIcon, PaperAirplaneIcon, RadioIcon, ArrowPathIcon
 } from '@heroicons/vue/24/outline';
import { AudioLines } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import router from '@/router';
import { openPlaylistInStudio } from '@/utils/studio';
import { appEvents, toggleFullscreen, toggleGesture } from '@/store/app.events';
import appStore from '@/store/app.store';
import chordsStore from '@/store/chords.store';
import transposeStore from '@/store/transpose.store';
import drawingStore from '@/store/drawing.store';

const STORAGE_KEY = 'menu-list-favorites';
const ChordsMenuList = {
    id: 'toggle-chords',
    label: 'Chords',
    group: 'playlist',
    icon: EyeIcon,
    shortcut: 'control + H',
    shortcutKeys: ['control', 'H'],
    toggleValue: ref(true),
    load: () => {
        appEvents.emit('currentChordsVisibility', chordsStore.state.visible);
    },
    onShortcut: () => {
        chordsStore.state.visible = !chordsStore.state.visible;
        appEvents.emit('currentChordsVisibility', chordsStore.state.visible);
    },
    onToggle: (value) => {
        chordsStore.state.visible = value;
        appEvents.emit('currentChordsVisibility', chordsStore.state.visible);
    },
    onExternalChange: (observer) => {
        appEvents.on('currentChordsVisibility', (value) => {
            observer('toggle-chords', {icon: value ? EyeIcon: EyeSlashIcon , toggleValue: value});
            chordsStore.state.visible = value;
        });
    },
    originalMenu: ChordsMenu,
    originalProps: ref({
        hidden: false,
        tooltipPosition: 'bottom',
        active: false,
        theme: 'light',
    }),
    events: {
        toggleChords: () => {
            appEvents.emit('toggleChords');
        }
    },
    ref: ref(null)
}
const ChordDiagramsMenuList = {
    id: 'toggle-chord-diagrams',
    label: 'Chord Diagrams',
    group: 'playlist',
    icon: TableCellsIcon,
    toggleValue: ref(false),
    onToggle: (value) => {
        appEvents.emit('toggleChordDiagrams', value);
    },
    onExternalChange: (observer) => {
        appEvents.on('currentChordDiagramsVisibility', (value) => {
            observer('toggle-chord-diagrams', { toggleValue: value });
        });
    },
    originalMenu: ChordDiagramsMenu,
    originalProps: ref({
        hidden: false,
        tooltipPosition: 'bottom',
        active: false,
        theme: 'light',
    }),
    ref: ref(null)
}

const enabledRealtimeToggle = ref(appStore.state.enabledRealtime);

if (appStore && typeof appStore.subscribe === 'function') {
    appStore.subscribe((mutation) => {
        if (mutation.type === 'SET_ENABLED_REALTIME') {
            enabledRealtimeToggle.value = mutation.payload;
            appEvents.emit('enabledRealtimeChanged', mutation.payload);
        }
    });
}

watch(
    () => appStore.state.enabledRealtime,
    (val) => {
        if (enabledRealtimeToggle.value !== val) {
            enabledRealtimeToggle.value = val;
            appEvents.emit('enabledRealtimeChanged', val);
        }
    }
);

export const menuList = [
    ChordsMenuList,
    ChordDiagramsMenuList,
    {
        id: 'hide-notes',
        label: 'Show Notes',
        group: 'song',
        icon: DocumentTextIcon,
        toggleValue: ref(true),
        onExternalChange: (observer) => {
            appEvents.on('toggleNotes', (value) => {  
                observer('hide-notes', { icon: value ? DocumentTextIcon : DocumentTextIcon, toggleValue: value});
            });
        },
        onToggle: (value) => {
            appEvents.emit('toggleNotes', value);
        },
        ref: ref(null)
    },
    {
        id: 'toggle-song-map',
        label: 'Song Map',
        group: 'playlist',
        icon: MapIcon,
        toggleValue: ref(true),
        onExternalChange: (observer) => {
            appEvents.on('toggleSongMap', (value) => {  
                observer('toggle-song-map', { toggleValue: value});
            });
        },
        onToggle: (value) => {
            appEvents.emit('toggleSongMap', value);
        },
        ref: ref(null)
    },
    {
        id: 'duplicate',
        label: 'Duplicate',
        group: 'playlist',
        icon: DocumentDuplicateIcon,
        shortcut: 'cmd + D',
        shortcutKeys: ['Meta', 'D'],
        onShortcut: () => {
            appEvents.emit('duplicate');
        },
        action: () => {
            appEvents.emit('duplicate');
        },
        ref: ref(null)
    },
    {
        id: 'edit-song',
        label: 'Edit song',
        group: 'song',
        icon: PencilIcon,
        shortcut: 'cmd + E',
        shortcutKeys: ['Meta', 'E'],
        onShortcut: () => {
            appEvents.emit('editSong');
        },
        action: () => {
            appEvents.emit('editSong');
        },
        ref: ref(null)
    },
    {
        id: 'play',
        label: 'Play',
        group: 'song',
        icon: PlayCircleIcon,
        iconProps: {
            class: 'text-red-500 dark:!text-red-400 hover:text-red-700 dark:hover:text-red-400'
        },
        shortcut: 'control + M',
        shortcutKeys: ['control', 'M'],
        onShortcut: () => {
            appEvents.emit('play');
        },
        action: () => {
            appEvents.emit('play');
        },
        ref: ref(null)
    },
    {
        id: 'columns-view',
        label: 'Columns view',
        group: 'song',
        icon: ViewColumnsIcon,
        shortcut: 'control + T',
        shortcutKeys: ['control', 'T'],
        onShortcut: () => {
            appEvents.emit('toggleColumns');
        },
        action: () => {
            appEvents.emit('toggleColumns');
        },
        originalMenu: ColumnsMenu,
        originalProps: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Change column view'
        }),
        ref: ref(null)
    },
    {
        id: 'fullscreen',
        label: 'Fullscreen',
        group: 'general',
        icon: ArrowsPointingOutIcon,
        toggleValue: ref(false),
        allowFavorite: false,
        onToggle: (value) => {
            toggleFullscreen(value);
        },
        onExternalChange: (observer) => {
            appEvents.on('toggleFullscreen', (value) => {
                observer('fullscreen', {icon: value ? ArrowsPointingInIcon : ArrowsPointingOutIcon, toggleValue: value});
            });
        },
        ref: ref(null)
    },
    {
        id: 'download',
        label: 'Download',
        group: 'playlist',
        icon: ArrowDownTrayIcon,
        action: () => {
            appEvents.emit('download');
        },
        originalMenu: DownloadMenu,
        originalProps: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Download PDF'
        }),
        ref: ref(null)
    },
    {
        id: 'transpose',
        label: 'Transpose',
        group: 'song',
        icon: ArrowsUpDownIcon,
        component: Transpose,
        keepOpen: true,
        shortcut: 'cmd + Shift + ↑/↓',
        shortcutKeys: [['Meta', 'Shift', 'ArrowUp'], ['Meta', 'Shift', 'ArrowDown']],
        onShortcut: (type) => {
            // Ignora atalhos enquanto o persist do transpose está em voo.
            if (transposeStore.state.busy) return;
            if (type === 'meta_shift_arrowup') {
                appEvents.emit('currentTranspose', {up: true, semitone: transposeStore.state.transpose + 1, current: 1});
            } else if (type === 'meta_shift_arrowdown') {
                appEvents.emit('currentTranspose', {up: false, semitone: transposeStore.state.transpose - 1, current: -1});
            }
        },
        props: ref({
            showLabel: false
        }),
        originalMenu: DropDownTranspose,
        originalProps: ref({
            showLabel: true,
        }),
        ref: ref(null)
    },
    {
        id: 'autoscroll',
        label: 'Auto scroll',
        group: 'song',
        icon: ArrowDownOnSquareIcon,
        component: ScrollMenu,
        keepOpen: true,
        props: ref({
            hidden: false,
            showLabel: false,
            showKeyboardShortcuts: false
        }),
        shortcut: 'cmd + Space',
        originalMenu: ScrollControl,
        originalProps: ref({
            scrollElement: ".card",
            disabled: false
        }),
        ref: ref(null)
    },
    {
        id: 'zoom',
        label: 'Zoom in/out',
        group: 'song',
        icon: MagnifyingGlassPlusIcon,
        component: ZoomInOutMenu,
        keepOpen: true,
        shortcut: 'control + / -',
        shortcutKeys: [['control', '='], ['control', '-']],
        onShortcut: (type) => {
            if (type === 'control_=') {
                appEvents.emit('zoomIn');
            } else if (type === 'control_-') {
                appEvents.emit('zoomOut');
            }
        },
        props: ref({
            hidden: false,
            tooltipPosition: 'left',
            zoomValue: 1
        }),
        fullMenu: ZoomInOutMenu,
        ref: ref(null)
    },
    {
        id: 'presentation',
        label: 'Presentation',
        group: 'playlist',
        icon: PresentationChartBarIcon,
        props: ref({
            hidden: false,
            tooltipPosition: 'left'
        }),
        shortcut: 'control + Shift + P',
        shortcutKeys: ['control', 'Shift', 'P'],
        onShortcut: () => {
            appEvents.emit('presentation');
        },
        action: () => {
            appEvents.emit('presentation');
        },
        ref: ref(null)
    },
    {
        id: 'drawing',
        label: 'Free hand drawing',
        group: 'user-notes',
        icon: PaintBrushIcon,
        props: ref({
            hidden: false,
            tooltipPosition: 'left'
        }),
        action: () => {
            appEvents.emit('toggleDrawing', !drawingStore.state.drawingEnabled);
        },
        shortcut: 'cmd + Shift + 0',
        shortcutKeys: ['Meta', 'Shift', '0'],
        onShortcut: () => {
            appEvents.emit('toggleDrawing', !drawingStore.state.drawingEnabled);
        },
        ref: ref(null)
    },
    {
        id: 'add-notes',
        label: 'Add Notes',
        group: 'user-notes',
        icon: DocumentPlusIcon,
        action: () => {
            appEvents.emit('addUserNotes');
        },
        ref: ref(null),
        shortcut: 'cmd + Shift + 9',
        shortcutKeys: ['Meta', 'Shift', '9'],
        onShortcut: () => {
            appEvents.emit('addUserNotes');
        },
    },
    {
        id: 'studio',
        label: 'Studio',
        group: 'song',
        icon: SpeakerWaveIcon,
        hidden: true,
        shortcut: 'control + Shift + S',
        shortcutKeys: ['control', 'Shift', 'S'],
        onShortcut: () => {
            appEvents.emit('toggleStudio');
        },
        action: () => {
            appEvents.emit('toggleStudio');
        },
        ref: ref(null)
    },
    {
        id: 'disable-gesture',
        label: 'Disable gesture',
        group: 'general',
        toggleValue: ref(appStore.state.disableGesture),
        allowFavorite: false,
        icon: HandRaisedIcon,
        onToggle: (value) => {
            toggleGesture(value);
        },
        onExternalChange: (observer) => {   
            appEvents.on('currentDisableGesture', (value) => {
                observer('disable-gesture', {toggleValue: value});
            });
        },
        ref: ref(null)
    },
    {
        id: 'floating-menu',
        label: 'Floating menu',
        group: 'general',
        toggleValue: ref(appStore.state.floatingMenu),
        allowFavorite: false,
        icon: CloudIcon,
        onToggle: (value) => {  
            appStore.commit('SET_FLOATING_MENU', value);
            appEvents.emit('toggleFloatingMenu', value);
        },
        ref: ref(null)
    },
    {
        id: 'realtime-update',
        label: 'Realtime update',
        group: 'general',
        toggleValue: enabledRealtimeToggle,
        allowFavorite: false,
        icon: ArrowPathIcon,
        load: () => {
            enabledRealtimeToggle.value = appStore.state.enabledRealtime;
        },
        onToggle: (value) => {
            appStore.commit('SET_ENABLED_REALTIME', value);
            appEvents.emit('enabledRealtimeChanged', value);
        },
        onExternalChange: (observer) => {
            appEvents.on('enabledRealtimeChanged', (value) => {
                observer('realtime-update', { toggleValue: value });
            });
        },
        ref: ref(null)
    },
    {
        id: 'open-studio',
        label: 'Open Studio',
        group: 'playlist',
        icon: AudioLines,
        action: () => {
            const playlistId = router.currentRoute.value?.params?.id;
            openPlaylistInStudio(playlistId);
        },
        ref: ref(null)
    },
    {
        id: 'share-playlist',
        label: 'Share playlist',
        group: 'playlist',
        icon: ShareIcon,
        originalMenu: ShareMenu,
        originalProps: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Share playlist'
        }),
        action: () => {
            appEvents.emit('sharePlaylist');
        },
        ref: ref(null)
    },
    {
        id: 'send-playlist-to-team',
        label: 'Send to team',
        group: 'playlist',
        icon: PaperAirplaneIcon,
        hidden: ref(true),
        load: () => {
            appEvents.emit('requestCanControlRealtime');
        },
        onExternalChange: (observer) => {
            appEvents.on('canControlRealtimeChanged', (value) => {
                observer('send-playlist-to-team', { hidden: !value });
            });
        },
        action: () => {
            appEvents.emit('sendPlaylistToTeam');
        },
        ref: ref(null)
    },
    {
        id: 'stage-sync',
        label: 'Stage sync (P2P / Cloud)',
        group: 'playlist',
        icon: RadioIcon,
        action: () => {
            appEvents.emit('open-stage-sync-modal');
        },
        ref: ref(null)
    },
    {
        id: 'tuner',
        label: 'Afinador',
        group: 'general',
        icon: TuningForkIcon,
        // Ferramenta global: acesso pelo sideMenu (Ferramentas). Oculto do popup da playlist.
        hidden: ref(true),
        action: () => {
            appEvents.emit('toggleTuner');
        },
        originalMenu: TunerMenu,
        originalProps: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Afinador'
        }),
        ref: ref(null)
    },
    {
        id: 'metronome',
        label: 'Metrônomo',
        group: 'song',
        icon: MetronomeIcon,
        // Ferramenta global: acesso pelo sideMenu (Ferramentas). Oculto do popup da playlist.
        hidden: ref(true),
        action: () => {
            appEvents.emit('toggleMetronome');
        },
        originalMenu: MetronomeMenu,
        originalProps: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Metrônomo'
        }),
        ref: ref(null)
    },
]

export const menuListFooter = {
    date: new Date().toLocaleDateString(),
    lastEditedBy: null
}
export const useMenus = (emit) => {
    const chordsMenu = {
        id: 'toggle-chords',
        shortcut: 'cmd + H',
        component: ChordsMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            active: false,
            theme: 'light',
            tooltipText: 'Toggle chords'
        }),
        value: true
    }
    const scrollControlMenu = {
        id: 'autoscroll',
        shortcut: 'cmd + Space',
        component: ScrollControl,
        props: ref({
            hidden: false,
            scrollElement: ".card",
        }),
    }
    const columnsMenu = {
        id: 'columns-view',
        shortcut: 'cmd + T',
        component: ColumnsMenu,
        props: ref({    
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Change column view'
        }),
        events: {
            toggleColumns: () => {
                appEvents.emit('toggleColumns');
            }
        }
    }
    const duplicateMenu = {
        id: 'duplicate',
        shortcut: 'cmd + D',
        component: DuplicateMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Duplicate Playlist'
        }),
        events: {
            duplicate: () => {  
                appEvents.emit('duplicate');
            }
        }
    }
    const presentationMenu = {
        id: 'presentation',
        component: PresentationMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            tooltipText: 'Presentation',
            theme: 'light'
        }),
        shortcut: 'cmd + Shift + P',
        events: {
            openPresentation: () => {
                appEvents.emit('presentation');
            }
        }
    }
    const playMenu = {
        id: 'play',
        shortcut: 'control + M',
        component: MediaMenu,
        props: ref({    
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Play song'
        }),
        events: {
            play: () => {
                appEvents.emit('play');
            }
        }
    }
    const notesMenu = {
        id: 'hide-notes',
        shortcut: 'cmd + N',
        component: NotesMenu,
        props: ref({
            active: true,
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Notes'
        })
    }

    const songMapMenu = {
        id: 'toggle-song-map',
        component: SongMapMenu,
        props: ref({
            active: true,
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Song Map'
        })
    }
    const downloadMenu = {
        id: 'download',
        shortcut: 'cmd + P',
        component: DownloadMenu,
        props: ref({
            hidden: false,  
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Download PDF'
        }),
        events: {
            download: () => {
                appEvents.emit('download');
            }
        }
    }
    const studioMenu = {
        id: 'studio',
        hidden: true,
        shortcut: 'cmd + Shift + S',
        component: StudioMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Studio'
        }),
        events: {
            toggleStudio: () => {
                appEvents.emit('toggleStudio');
            }
        }
    }
    const transposeMenu = {
        id: 'transpose',
        shortcut: 'cmd + Shift + ↑/↓',
        component: DropdownTranspose,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Transpose',
            showLabel: true
        }),
        events: {
            onTranspose: (value) => {
                emit('event', 'onTranspose', value)
            }
        }
    }
    const userNotesMenu = {
        id: 'add-notes',
        shortcut: 'cmd + Shift + P',
        component: AddNotes,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Add user notes'
        }),
        events: {
            addUserNotes: () => {
                appEvents.emit('addUserNotes');
            }
        }
    }
    const drawingMenu = {
        id: 'drawing',
        shortcut: 'cmd + Shift + P',
        component: DrawingMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Realtime drawing'
        }),
        events: {
            toggleDrawing: () => {
                emit('event', 'toggleDrawing')
                appEvents.emit('toggleDrawing', true);
            }
        }
    }
    const zoomInOutMenu = {
        id: 'zoom',
        shortcut: 'cmd + / -',
        component: ZoomInOutMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Zoom in/out',
            zoomValue: 1
        }),
        events: {
            zoomIn: () => {
                emit('event', 'zoomIn')
            },
            zoomOut: () => {
                emit('event', 'zoomOut')
            }
        }
    }
    const shareMenu = {
        id: 'share-playlist',
        shortcut: 'cmd + Shift + P',
        component: ShareMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Share playlist'
        }),
    }
    const stageSyncMenu = {
        id: 'stage-sync',
        component: StageSyncMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Stage sync (P2P / Cloud)'
        }),
        events: {
            openStageSync: () => {
                appEvents.emit('open-stage-sync-modal');
            }
        }
    }
    const sendPlaylistToTeamMenu = {
        id: 'send-playlist-to-team',
        component: SendPlaylistToTeamMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Send to team'
        }),
    }
    const editSongMenu = {
        id: 'edit-song',
        shortcut: 'cmd + Shift + E',
        component: EditSongMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Edit song'
        }),
    }
    const chordDiagramsMenu = {
        id: 'toggle-chord-diagrams',
        component: ChordDiagramsMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            active: false,
            theme: 'light',
        }),
    }
    const tunerMenu = {
        id: 'tuner',
        component: TunerMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Afinador'
        }),
        events: {
            toggleTuner: () => {
                appEvents.emit('toggleTuner');
            }
        }
    }
    const metronomeMenu = {
        id: 'metronome',
        component: MetronomeMenu,
        props: ref({
            hidden: false,
            tooltipPosition: 'bottom',
            theme: 'light',
            tooltipText: 'Metrônomo'
        }),
        events: {
            toggleMetronome: () => {
                appEvents.emit('toggleMetronome');
            }
        }
    }
    return {
        chordsMenu, 
        scrollControlMenu,
        columnsMenu,
        downloadMenu,
        duplicateMenu,
        editSongMenu,
        presentationMenu,
        playMenu,
        notesMenu,
        studioMenu,
        zoomInOutMenu,
        transposeMenu,
        userNotesMenu,
        drawingMenu,
        shareMenu,
        stageSyncMenu,
        sendPlaylistToTeamMenu,
        songMapMenu,
        chordDiagramsMenu,
        tunerMenu,
        metronomeMenu
    }
}

export const listAccessMenusRestricted = ['edit-song', 'share-playlist'];

export const fetchFavoriteMenuIds = () => {
    let favoriteMenuIds = [];
    
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            favoriteMenuIds = JSON.parse(saved);
        } else {
            favoriteMenuIds = ['toggle-chords', 'transpose', 'zoom-in-out', 'share-playlist'];
        }
    } catch (error) {
        console.error('Error loading favorites:', error)
    }
    return favoriteMenuIds;
}

export const saveFavoriteMenuIds = (favoriteMenuIds = []) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favoriteMenuIds))
    } catch (error) {
        console.error('Error saving favorites:', error)
    }
}